import type { Extension, ObservationDefinition, ValueSet } from "@medplum/fhirtypes";
import { asMemberId, type StoredFindingDefinitionProps } from "@repo/domain";
import { codeSystemUrl, extensionUrl, FINDING_LINEAGE_SYSTEM, OBSERVATION_CATEGORY_SYSTEM } from "@repo/fhir-systems";
import { FINDING_DEFINITION_LINEAGE_SYSTEM, FINDING_DEFINITION_REVISION_SYSTEM } from "@repo/fhir-systems/identifiers";
import { catalogModelKindTag, FINDING_DEFINITION_KIND } from "../lib/catalog-kind";
import { CATALOG_VALUE_URL, decodeCatalogValue, encodeCatalogValue } from "../lib/catalog-value-extension";

import { EXAM_FINDING_OPTION_SYSTEM } from "./finding-option-system";

import { required } from "./read";

// ============================================================
// FindingDefinition ⇄ standalone ObservationDefinition
//
// R4 has no canonical spine on ObservationDefinition. Code carries the shared
// finding lineage; the spine extension carries revision and authoring facts. A contained ValueSet owns
// options; validCodedValueSet references it. The one normal option, when there
// is one, is also listed in a second contained ValueSet that normalCodedValueSet
// references — R4's own slot for "the normal coded results". Each revision is a
// new resource.
// Cardinality and the option ValueSet are choice facts: a measured row writes
// neither. code.text mirrors the spine name for FHIR readers; nothing reads it back.
// Diagnostic measurement definitions remain in their own contained registry.
// ============================================================

export const FINDING_COMPONENT_MARK_URL = extensionUrl("finding-definition-component-mark");
export const FINDING_COMPONENTS_URL = extensionUrl("finding-definition-components");
export const CATALOG_FINDING_DEFINITION_PROFILE = extensionUrl("catalog-finding-definition");
export const FINDING_DEFINITION_MEMBER_MARKS_URL = extensionUrl("finding-definition-member-marks");
export const FINDING_DEFINITION_SPINE_URL = extensionUrl("finding-definition-spine");
const FINDING_OPTION_DETAIL_URL = extensionUrl("finding-option-detail");
export const EXAM_RESERVED_RESPONSE_SYSTEM = codeSystemUrl("exam-reserved-response");
export { EXAM_FINDING_OPTION_SYSTEM };
type Props = StoredFindingDefinitionProps;
const OWNED = new Set<string>([
  FINDING_COMPONENTS_URL,
  FINDING_COMPONENT_MARK_URL,
  FINDING_DEFINITION_MEMBER_MARKS_URL,
  FINDING_DEFINITION_SPINE_URL,
]);

export function writeFindingDefinitionRow(props: Props, prior: ObservationDefinition | null): ObservationDefinition {
  const choice = props.kind === "Choice" ? props : undefined;
  return {
    resourceType: "ObservationDefinition",
    ...(prior
      ? {}
      : {
          meta: { profile: [CATALOG_FINDING_DEFINITION_PROFILE], tag: [catalogModelKindTag(FINDING_DEFINITION_KIND)] },
        }),
    identifier: [
      { system: FINDING_DEFINITION_LINEAGE_SYSTEM, value: props.spine.url },
      { system: FINDING_DEFINITION_REVISION_SYSTEM, value: String(props.spine.version) },
    ],
    category: [{ coding: [{ system: OBSERVATION_CATEGORY_SYSTEM, code: "exam" }] }],
    code: { text: props.spine.name, coding: [{ system: FINDING_LINEAGE_SYSTEM, code: props.spine.url }] },
    ...(choice
      ? {
          multipleResultsAllowed: choice.cardinality === "Multiple",
          validCodedValueSet: { reference: "#options" },
          ...(normalOption(choice) ? { normalCodedValueSet: { reference: "#normal" } } : {}),
          contained: [
            optionsValueSet(choice, props.spine.availability),
            ...normalValueSet(choice, props.spine.availability),
          ],
        }
      : {}),
    extension: [
      ...(props.kind === "Measured"
        ? [
            {
              url: FINDING_COMPONENTS_URL,
              extension: props.components.map((component) => ({
                url: "component",
                extension: [
                  { url: "id", valueString: component.id },
                  { url: "label", valueString: component.label },
                  encodeCatalogValue(component.value),
                ],
              })),
            },
          ]
        : []),
      ...(prior?.extension ?? []).filter((ext) => !OWNED.has(ext.url)),
      {
        url: FINDING_DEFINITION_SPINE_URL,
        extension: [
          { url: "name", valueString: props.spine.name },
          { url: "version", valueInteger: props.spine.version },
          { url: "mintedAt", valueDateTime: props.spine.mintedAt },
          { url: "availability", valueCode: props.spine.availability },
          ...(props.spine.description === undefined
            ? []
            : [{ url: "description", valueString: props.spine.description }]),
        ],
      },
      ...(props.marks ? [{ url: FINDING_DEFINITION_MEMBER_MARKS_URL, valueInteger: props.marks.options }] : []),
      ...(props.marks?.components ? [{ url: FINDING_COMPONENT_MARK_URL, valueInteger: props.marks.components }] : []),
    ],
  };
}

function optionsValueSet(
  choice: Extract<Props, { kind: "Choice" }>,
  availability: Props["spine"]["availability"],
): ValueSet {
  return {
    resourceType: "ValueSet",
    id: "options",
    status: availability === "Active" ? "active" : "retired",
    compose: {
      include: [
        {
          system: EXAM_FINDING_OPTION_SYSTEM,
          concept: choice.options.map((option) => ({
            code: option.id,
            display: option.label,
            ...(option.detail === undefined
              ? {}
              : { extension: [{ url: FINDING_OPTION_DETAIL_URL, valueString: option.detail }] }),
          })),
        },
        {
          system: EXAM_RESERVED_RESPONSE_SYSTEM,
          concept: choice.offerOther ? [{ code: "other", display: "Other" }] : [],
        },
      ].filter((include) => include.concept.length > 0),
    },
  };
}

function normalOption(choice: Extract<Props, { kind: "Choice" }>) {
  return choice.options.find((option) => option.normal);
}

function normalValueSet(
  choice: Extract<Props, { kind: "Choice" }>,
  availability: Props["spine"]["availability"],
): ValueSet[] {
  const normal = normalOption(choice);
  if (!normal) return [];
  return [
    {
      resourceType: "ValueSet",
      id: "normal",
      status: availability === "Active" ? "active" : "retired",
      compose: {
        include: [{ system: EXAM_FINDING_OPTION_SYSTEM, concept: [{ code: normal.id, display: normal.label }] }],
      },
    },
  ];
}

export function readFindingDefinitionRow(row: ObservationDefinition): Props {
  const ext = (url: string): Extension | undefined => row.extension?.find((candidate) => candidate.url === url);
  const spine = (url: string) =>
    ext(FINDING_DEFINITION_SPINE_URL)?.extension?.find((candidate) => candidate.url === url);
  const description = spine("description")?.valueString;
  const marks = ext(FINDING_DEFINITION_MEMBER_MARKS_URL)?.valueInteger;
  const common = {
    spine: {
      url: required(row, "url", row.code.coding?.find((coding) => coding.system === FINDING_LINEAGE_SYSTEM)?.code),
      version: Number(required(row, "version", spine("version")?.valueInteger)),
      name: required(row, "name", spine("name")?.valueString),
      mintedAt: required(row, "mintedAt", spine("mintedAt")?.valueDateTime),
      availability: spine("availability")?.valueCode === "Active" ? ("Active" as const) : ("Inactive" as const),
      ...(description === undefined ? {} : { description }),
    },
    ...(marks === undefined
      ? {}
      : { marks: { options: marks, components: ext(FINDING_COMPONENT_MARK_URL)?.valueInteger ?? 0 } }),
  };
  const measured = ext(FINDING_COMPONENTS_URL);
  if (measured)
    return {
      kind: "Measured",
      ...common,
      components: (measured.extension ?? []).map((component) => ({
        id: asMemberId(required(row, "component.id", component.extension?.find((e) => e.url === "id")?.valueString)),
        label: required(row, "component.label", component.extension?.find((e) => e.url === "label")?.valueString),
        value: decodeCatalogValue(component.extension?.find((e) => e.url === CATALOG_VALUE_URL)),
      })),
    };
  const contained = (reference: string | undefined) =>
    row.contained?.find(
      (candidate): candidate is ValueSet => candidate.resourceType === "ValueSet" && `#${candidate.id}` === reference,
    );
  const includes = required(row, "options", contained(row.validCodedValueSet?.reference)?.compose?.include);
  // A row written before normal was a designation also lists a reserved `wnl`
  // concept here; it is not an option and nothing reads it.
  const reserved = includes
    .filter((include) => include.system === EXAM_RESERVED_RESPONSE_SYSTEM)
    .flatMap((include) => include.concept ?? [])
    .map((concept) => concept.code);
  const normal = new Set(
    (contained(row.normalCodedValueSet?.reference)?.compose?.include ?? [])
      .filter((include) => include.system === EXAM_FINDING_OPTION_SYSTEM)
      .flatMap((include) => include.concept ?? [])
      .map((concept) => concept.code),
  );
  return {
    kind: "Choice",
    ...common,
    cardinality: row.multipleResultsAllowed ? "Multiple" : "Single",
    offerOther: reserved.includes("other"),
    options: includes
      .filter((include) => include.system === EXAM_FINDING_OPTION_SYSTEM)
      .flatMap((include) => include.concept ?? [])
      .map((option) => {
        const detail = option.extension?.find((e) => e.url === FINDING_OPTION_DETAIL_URL)?.valueString;
        return {
          id: asMemberId(option.code),
          label: required(row, "option.label", option.display),
          ...(detail === undefined ? {} : { detail }),
          ...(normal.has(option.code) ? { normal: true as const } : {}),
        };
      }),
  };
}
